import React, { useEffect, useState, useMemo, useContext } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  TableSortLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { collection, query, onSnapshot, deleteDoc, doc, addDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import ConfirmationDialog from './ConfirmationDialog';
import InfoModalV2 from '../components/InfoModalV2';
import { createSolutionSchema } from '../modals/SolutionSchema';
import useIsMobile from '../utils/useIsMobile';
import { AppContext } from '../AppContext';
import AddSolution from './AddSolution';

function Solution({ setError, setSuccess }) {
  const { user, userData } = useContext(AppContext);
  const activeFarmId = userData?.activeFarmId;
  const isMobile = useIsMobile();
  const [solutions, setSolutions] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [solutionToEdit, setSolutionToEdit] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedSolution, setSelectedSolution] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSolution, setConfirmSolution] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [selectedSolutionData, setSelectedSolutionData] = useState(null);

  const [solutionsSortColumn, setSolutionsSortColumn] = useState('name');
  const [solutionsSortDirection, setSolutionsSortDirection] = useState('asc');

  useEffect(() => {
    if (!user || !activeFarmId) {
      setSolutions([]);
      return;
    }
    const ref = collection(db, 'farms', activeFarmId, 'solutions');
    const q = query(ref);
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setSolutions(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
    return unsubscribe;
  }, [user, activeFarmId]);

  const filteredSolutions = useMemo(() => {
    return solutions.filter((solution) =>
      solution.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [solutions, searchTerm]);

  const getPacketVolumeString = (solution) => {
    const volume = solution.packetVolume ?? solution.packetWeight;
    const unit = solution.volumeUnit || solution.weightUnit || '';
    if (!volume) return 'N/A';
    return `${volume}${unit}`.trim();
  };

  const getPacketCostString = (solution) => {
    if (!solution.packetCost || isNaN(parseFloat(solution.packetCost))) return 'N/A';
    return solution.costCurrency ? `${solution.costCurrency}${solution.packetCost}` : solution.packetCost;
  };

  const sortedSolutions = useMemo(() => {
    const sorted = [...filteredSolutions].sort((a, b) => {
      let aValue, bValue;
      switch (solutionsSortColumn) {
        case 'name':
          aValue = a.name ? a.name.toLowerCase() : '';
          bValue = b.name ? b.name.toLowerCase() : '';
          break;
        case 'packetCost':
          aValue = parseFloat(a.packetCost) || 0;
          bValue = parseFloat(b.packetCost) || 0;
          break;
        case 'packetVolume':
          aValue = parseFloat(a.packetVolume ?? a.packetWeight) || 0;
          bValue = parseFloat(b.packetVolume ?? b.packetWeight) || 0;
          break;
        default:
          aValue = '';
          bValue = '';
      }
      if (aValue < bValue) return solutionsSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return solutionsSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredSolutions, solutionsSortColumn, solutionsSortDirection]);

  const paginatedSolutions = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedSolutions.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedSolutions, page, rowsPerPage]);

  const handleSolutionsSort = (column) => {
    if (solutionsSortColumn === column) {
      setSolutionsSortDirection(solutionsSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSolutionsSortColumn(column);
      setSolutionsSortDirection('asc');
    }
  };

  const handleOpen = () => setOpen(true);
  const handleCloseDialog = () => {
    setOpen(false);
    setEditMode(false);
    setSolutionToEdit(null);
  };
  const handleAddSolution = () => {
    handleCloseDialog();
  };

  const handleClone = async () => {
    if (!selectedSolution || !user) return;
    if (!activeFarmId) {
      setError?.('No active farm selected.');
      return;
    }
    try {
      const { id, ...dataToClone } = selectedSolution;
      await addDoc(collection(db, 'farms', activeFarmId, 'solutions'), dataToClone);
      setSuccess?.('Solution cloned successfully');
    } catch (err) {
      setError?.('Failed to clone solution.');
    }
    handleMenuClose();
  };

  const handleDelete = async (id) => {
    if (!user) return;
    if (!activeFarmId) {
      setError?.('No active farm selected.');
      return;
    }
    try {
      await deleteDoc(doc(db, 'farms', activeFarmId, 'solutions', id));
      setSuccess?.('Solution deleted successfully');
    } catch (error) {
      console.error('Error deleting solution:', error);
      setError?.('Failed to delete solution');
    }
    handleMenuClose();
  };

  const openConfirmDialog = (solution) => {
    setConfirmSolution(solution);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (confirmSolution) {
      await handleDelete(confirmSolution.id);
    }
    setConfirmOpen(false);
    setConfirmSolution(null);
  };

  const handleConfirmCancel = () => {
    setConfirmOpen(false);
    setConfirmSolution(null);
  };

  const handleMenuOpen = (event, solution) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedSolution(solution);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedSolution(null);
  };

  const handleEdit = () => {
    setEditMode(true);
    setSolutionToEdit(selectedSolution);
    handleOpen();
    handleMenuClose();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (solution) => {
    setSelectedSolutionData(solution);
    setInfoModalOpen(true);
  };

  const solutionSchema = useMemo(
    () => createSolutionSchema({ getPacketVolumeString, getPacketCostString }),
    [getPacketVolumeString, getPacketCostString]
  );

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
    '& .MuiTableSortLabel-root': {
      color: `${theme.palette.common.white} !important`,
      '&.Mui-active': {
        color: `${theme.palette.common.white} !important`,
      },
      '& .MuiTableSortLabel-icon': {
        color: `${theme.palette.common.white} !important`,
      },
      '&:hover': {
        color: 'rgba(255, 255, 255, 0.75) !important',
      },
    },
  }));

  const subscriptionActive = isSubscriptionActive(userData);

  return (
    <Box
      sx={{
        backgroundColor: (theme) => theme.palette.background.default,
        color: (theme) => theme.palette.text.primary,
        display: 'flex',
        flexDirection: 'column',
        padding: '20px',
        width: '100%',
        maxWidth: '80%',
        margin: '0 auto',
        '@media (max-width: 600px)': { maxWidth: '100%' },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: (theme) => theme.palette.background.default,
          zIndex: -1,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Solutions</Typography>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleOpen}
          disabled={!subscriptionActive}
        >
          Add Solution
        </Button>
      </Box>
      {isMobile && (
        <Paper sx={{ mb: 2 }}>
          <List disablePadding>
            {paginatedSolutions.map((solution) => (
              <React.Fragment key={solution.id}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleRowClick(solution)} sx={{ alignItems: 'flex-start' }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2">{solution.name || 'N/A'}</Typography>
                          <Chip size="small" color="info" label={getPacketCostString(solution)} />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            {getPacketVolumeString(solution)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
            {paginatedSolutions.length === 0 && (
              <ListItem>
                <ListItemText primary="No solutions found." />
              </ListItem>
            )}
          </List>
        </Paper>
      )}
      <TableContainer component={Paper} sx={{ display: { xs: 'none', sm: 'block' } }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <TableSortLabel
                  active={solutionsSortColumn === 'name'}
                  direction={solutionsSortColumn === 'name' ? solutionsSortDirection : 'asc'}
                  onClick={() => handleSolutionsSort('name')}
                >
                  Name
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={solutionsSortColumn === 'packetCost'}
                  direction={solutionsSortColumn === 'packetCost' ? solutionsSortDirection : 'asc'}
                  onClick={() => handleSolutionsSort('packetCost')}
                >
                  Packet Cost
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={solutionsSortColumn === 'packetVolume'}
                  direction={solutionsSortColumn === 'packetVolume' ? solutionsSortDirection : 'asc'}
                  onClick={() => handleSolutionsSort('packetVolume')}
                >
                  Packet Volume
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Notes</span>
                  <Tooltip
                    title={
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        Mouse over note to read entire entry.
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
              </StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedSolutions.map((solution) => (
              <TableRow key={solution.id} onClick={() => handleRowClick(solution)} sx={{ cursor: 'pointer' }}>
                <TableCell>{solution.name || 'N/A'}</TableCell>
                <TableCell>{getPacketCostString(solution)}</TableCell>
                <TableCell>{getPacketVolumeString(solution)}</TableCell>
                <TableCell
                  sx={{
                    maxWidth: 100,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Tooltip title={<Typography sx={{ fontSize: '0.8rem' }}>{solution.notes || ''}</Typography>}>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        width: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {solution.notes || ''}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <IconButton onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, solution); }}>
                    <SettingsIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {paginatedSolutions.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No solutions found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredSolutions.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem disabled={!subscriptionActive} onClick={handleEdit}>Edit</MenuItem>
        <MenuItem disabled={!subscriptionActive} onClick={handleClone}>Clone</MenuItem>
        <MenuItem onClick={() => { handleMenuClose(); openConfirmDialog(selectedSolution); }}>
          Delete
        </MenuItem>
      </Menu>
      <AddSolution
        open={open}
        onClose={handleCloseDialog}
        onAdd={handleAddSolution}
        editMode={editMode}
        solutionToEdit={solutionToEdit}
        setSuccess={setSuccess}
        setError={setError}
      />
      <ConfirmationDialog
        open={confirmOpen}
        onClose={handleConfirmCancel}
        onConfirm={handleConfirmDelete}
        title="Delete Solution"
        message="Are you sure you want to delete this solution?"
      />
      <InfoModalV2
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        data={selectedSolutionData}
        schema={solutionSchema}
      />
    </Box>
  );
}

export default Solution;

function isSubscriptionActive(userData) {
  if (!userData || !userData.accessExpires) {
    return true;
  }
  const now = new Date();
  const expires = userData.accessExpires.toDate
    ? userData.accessExpires.toDate()
    : new Date(userData.accessExpires);
  return now <= expires;
}


